// "Catch up": fill in the last few hours in one go when nobody logged.
// Rough is fine — it keeps predictions and charts sensible.

import { useMemo, useState } from 'react';
import { logEvent } from '../../data/actions.ts';
import { useLiveStatus } from '../../data/hooks.ts';
import { HOUR, MINUTE, formatTime } from '../../domain/time.ts';
import { Button, Chips, Field, Segmented, Sheet } from '../../ui/primitives.tsx';
import { toast } from '../../ui/toast.tsx';
import { Stepper } from './EventEditor.tsx';

export function CatchUp({ open, onClose, since }: { open: boolean; onClose: () => void; since?: number }) {
  const s = useLiveStatus(60_000);
  const [hours, setHours] = useState(() => (since ? Math.min(12, Math.max(1, Math.round((Date.now() - since) / HOUR))) : 3));
  const [wees, setWees] = useState(2);
  const [poos, setPoos] = useState(1);
  const [accidents, setAccidents] = useState(0);
  const from = s.now - hours * HOUR;
  const missedMeals = useMemo(() => s.meals.meals.filter((m) => !m.eaten && m.at >= from && m.at <= s.now), [s.meals, from, s.now]);
  const [meals, setMeals] = useState<string[]>(() => missedMeals.map((m) => m.time));
  const [sleeping, setSleeping] = useState<'now' | 'napped' | 'no'>(s.sleep.asleep ? 'now' : 'napped');

  const save = () => {
    const spread = (n: number, offset: number) => Array.from({ length: n }, (_, i) => from + ((i + offset) / (n + 1)) * (s.now - from));
    const opts = { note: 'catch-up (approximate)' };
    spread(wees, 1).forEach((at) => logEvent('wee', { where: 'outside' }, { ...opts, at }));
    spread(poos, 0.6).forEach((at) => logEvent('poo', { where: 'outside' }, { ...opts, at }));
    spread(accidents, 0.8).forEach((at) => logEvent('wee', { where: 'inside' }, { ...opts, at }));
    for (const m of missedMeals.filter((m) => meals.includes(m.time))) logEvent('meal', { grams: m.grams, appetite: 'all' }, { ...opts, at: m.at });
    if (sleeping === 'napped') logEvent('sleep', {}, { ...opts, at: s.now - Math.min(hours * HOUR, 110 * MINUTE), end: s.now - 20 * MINUTE });
    if (sleeping === 'now' && !s.sleep.asleep) logEvent('sleep', {}, { ...opts, at: s.now - 10 * MINUTE });
    toast({ message: 'Caught up', detail: 'Thanks — predictions pick up from here.', tone: 'good' });
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Catch up"
      footer={
        <>
          <span className="spacer" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <p className="muted">Busy few hours? Roughly what happened — approximate is fine. It keeps the reminders and charts sensible.</p>
      <div className="form">
        <Field label="Over the last">
          <Segmented
            value={hours}
            onChange={setHours}
            options={[1, 2, 3, 4, 6, 8].map((h) => ({ value: h, label: `${h} h` }))}
          />
        </Field>
        <div className="form-grid">
          <Field label="Wees outside">
            <Stepper value={wees} onChange={setWees} showZero />
          </Field>
          <Field label="Poos outside">
            <Stepper value={poos} onChange={setPoos} showZero />
          </Field>
          <Field label="Accidents indoors">
            <Stepper value={accidents} onChange={setAccidents} showZero />
          </Field>
        </div>
        {missedMeals.length > 0 && (
          <Field label="Meals he had">
            <Chips value={meals} onChange={setMeals} options={missedMeals.map((m) => ({ value: m.time, label: `${formatTime(m.at)}${m.grams ? ` · ${m.grams} g` : ''}` }))} />
          </Field>
        )}
        <Field label="Sleep">
          <Segmented
            value={sleeping}
            onChange={setSleeping}
            options={[
              { value: 'napped', label: 'Had a nap, awake now' },
              { value: 'now', label: 'Asleep now' },
              { value: 'no', label: 'No nap' },
            ]}
          />
        </Field>
      </div>
    </Sheet>
  );
}
